/**
 * @module brandGuide
 * @description Brand guidelines and theme configuration for the application.
 * Defines colors, typography, spacing, animations, and other design tokens.
 * Updated for the new Entertainment Lighting visual identity.
 */

/**
 * Brand color palette
 */
const colors = {
  primary: '#7C3AED', // Purple
  secondary: '#1E1B4B', // Deep Blue
  accent: {
    cyan: '#06B6D4',
    pink: '#EC4899',
    purple: '#7C3AED'
  },
  neutral: {
    white: '#FFFFFF',
    black: '#000000',
    gray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827'
    }
  },
  // Dark theme specifics
  dark: {
    DEFAULT: '#0F0F23',
    lighter: '#1E1B4B',
    card: '#1A1A2E'
  }
};

export const brandGuide = {
  colors: {
    primary: {
      DEFAULT: '#7C3AED',
      light: '#A78BFA',
      dark: '#6D28D9'
    },
    secondary: {
      DEFAULT: '#1E1B4B',
      light: '#312E81',
      dark: '#0F0F23'
    },
    accent: {
      cyan: {
        light: '#22D3EE',
        DEFAULT: '#06B6D4',
        dark: '#0891B2'
      },
      pink: {
        light: '#F472B6',
        DEFAULT: '#EC4899',
        dark: '#DB2777'
      }
    },
    neutral: {
      white: '#FFFFFF',
      black: '#000000',
      gray: {
        50: '#F9FAFB',
        100: '#F3F4F6',
        200: '#E5E7EB',
        300: '#D1D5DB',
        400: '#9CA3AF',
        500: '#6B7280',
        600: '#4B5563',
        700: '#374151',
        800: '#1F2937',
        900: '#111827'
      }
    },
    gradients: {
      primary: 'linear-gradient(to right, #7C3AED, #EC4899)', // Purple to Pink
      secondary: 'linear-gradient(to right, #06B6D4, #7C3AED)', // Cyan to Purple
      dark: 'linear-gradient(to bottom, #1E1B4B, #0F0F23)', // Deep Blue gradient
      glass: 'linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))',
      laser: 'linear-gradient(90deg, transparent, #06B6D4, transparent)' // Laser effect
    }
  },
  typography: {
    fontFamily: {
      sans: ['Inter', 'system-ui', 'sans-serif'],
      heading: ['Outfit', 'sans-serif'], // Assuming Outfit is used for headings based on "modern typography" reqs
      mono: ['JetBrains Mono', 'monospace'],
    },
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700
    },
    fontSize: {
      xs: '0.75rem',     // 12px
      sm: '0.875rem',    // 14px
      base: '1rem',      // 16px
      lg: '1.125rem',    // 18px
      xl: '1.25rem',     // 20px
      '2xl': '1.5rem',   // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem',  // 36px
      '5xl': '3rem',     // 48px
      '6xl': '3.75rem',  // 60px
      '7xl': '4.5rem',   // 72px
    },
  },
  layout: {
    maxWidth: {
      content: '1280px',
      wide: '1536px',
    },
    zIndex: {
      behind: -1,
      base: 0,
      raised: 1,
      dropdown: 1000,
      sticky: 1100,
      overlay: 1300,
      modal: 1400,
    }
  },
  visual: {
    borderRadius: {
      none: '0',
      sm: '0.25rem',
      md: '0.375rem',
      lg: '0.5rem',
      xl: '0.75rem',
      '2xl': '1rem',
      full: '9999px'
    },
    blur: {
      none: '0',
      sm: '4px',
      md: '8px',
      lg: '12px',
      xl: '16px',
    },
  },
  animation: {
    duration: {
      fast: '150ms',
      normal: '300ms',
      slow: '500ms',
      slower: '700ms',
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    },
  },
  components: {
    button: {
      variants: {
        primary: 'bg-[#7C3AED] hover:bg-[#6D28D9] text-white shadow-[0_0_15px_rgba(124,58,237,0.5)]',
        secondary: 'bg-[#1E1B4B] hover:bg-[#312E81] text-white border border-[#7C3AED]/30',
        outline: 'border border-[#06B6D4] text-[#06B6D4] hover:bg-[#06B6D4]/10',
        ghost: 'text-[#EC4899] hover:bg-[#EC4899]/10'
      }
    },
    card: {
      variants: {
        default: 'bg-white border border-gray-200',
        interactive: 'bg-white border border-gray-200 hover:border-[#7C3AED]/20 hover:shadow-lg',
        glass: 'bg-white/80 backdrop-blur-lg border border-white/20 hover:bg-white/90'
      },
      padding: {
        sm: 'p-4',
        md: 'p-6',
        lg: 'p-8'
      }
    },
    skeleton: {
      variants: {
        default: 'bg-gray-200 animate-pulse rounded-md',
        card: 'bg-gray-200 animate-pulse rounded-xl',
        text: 'bg-gray-200 animate-pulse rounded h-4',
        title: 'bg-gray-200 animate-pulse rounded-md h-8',
        avatar: 'bg-gray-200 animate-pulse rounded-full',
        button: 'bg-gray-200 animate-pulse rounded-lg h-10',
        input: 'bg-gray-200 animate-pulse rounded-lg h-10'
      }
    }
  }
};